/**
 * Professional Route Translation System
 * Handles bidirectional translation between English and Arabic routes
 */

// Complete route mapping between languages
export const routeTranslations = {
  en: {
    // Main pages
    'نبذة-عنا': 'about-us',
    'مشاريعنا': 'projects',
    'خدماتنا': 'service',
    'عملائنا': 'clients',
    'المدونات': 'blogs',
    'وظائف': 'jobs',
    'اتصل-بنا': 'contactus',
    'الصفحة-الرئيسية': 'home',
    
    // SEO pages
    'العلامات-التجارية': 'branding',
    'تطوير-الموقع': 'website-development',
    'تسويق-وسائل-الاعلام-الاجتماعية': 'social-media-marketing',
    'تسويق-المحتوى': 'content-marketing',
    'الإنتاج-المرئي': 'visual-production',
    
    // Special routes
    'الاتصال-بالخدمة': 'serviceContact'
  },
  ar: {
    // Main pages
    'about-us': 'نبذة-عنا',
    'projects': 'مشاريعنا',
    'service': 'خدماتنا',
    'clients': 'عملائنا',
    'blogs': 'المدونات',
    'jobs': 'وظائف',
    'contactus': 'اتصل-بنا',
    'home': 'الصفحة-الرئيسية',
    
    // SEO pages
    'branding': 'العلامات-التجارية',
    'website-development': 'تطوير-الموقع',
    'social-media-marketing': 'تسويق-وسائل-الاعلام-الاجتماعية',
    'content-marketing': 'تسويق-المحتوى',
    'visual-production': 'الإنتاج-المرئي',
    
    // Special routes
    'serviceContact': 'الاتصال-بالخدمة'
  }
};

/**
 * Translates a route from one language to another
 * @param {string} pathname - Current pathname (e.g., '/en/about-us')
 * @param {string} targetLang - Target language ('en' or 'ar')
 * @returns {string} Translated pathname
 */
export const translateRoute = (pathname, targetLang) => {
  // Validate target language
  if (targetLang !== 'en' && targetLang !== 'ar') {
    console.warn(`Invalid target language: ${targetLang}. Returning original pathname.`);
    return pathname;
  }

  const translations = routeTranslations[targetLang];
  if (!translations) return pathname;

  // Decode pathname if it's URL-encoded (React Router usually decodes it, but handle both cases)
  let decodedPathname;
  try {
    decodedPathname = decodeURIComponent(pathname);
  } catch (e) {
    decodedPathname = pathname;
  }

  // Normalize pathname - remove trailing slash except for root
  let normalizedPath = decodedPathname;
  if (normalizedPath.endsWith('/') && normalizedPath.length > 1) {
    normalizedPath = normalizedPath.slice(0, -1);
  }

  // Handle root path
  if (normalizedPath === '/' || normalizedPath === '') {
    return `/${targetLang}`;
  }

  // Split path into parts
  const pathParts = normalizedPath.split('/').filter(Boolean);
  
  // If no parts, return target language root
  if (pathParts.length === 0) {
    return `/${targetLang}`;
  }

  const currentLang = pathParts[0];
  
  // If already in target language, return as is
  if (currentLang === targetLang) {
    return normalizedPath;
  }

  // Get route segments (everything after language prefix)
  const routeSegments = pathParts.slice(1);
  
  // If no route segments, just return target language root
  if (routeSegments.length === 0) {
    return `/${targetLang}`;
  }

  // Decode each segment to handle URL encoding
  const decodedSegments = routeSegments.map(segment => {
    try {
      // Try to decode if it contains % (URL-encoded)
      if (segment.includes('%')) {
        return decodeURIComponent(segment);
      }
      return segment;
    } catch (e) {
      return segment;
    }
  });

  // Translate the first segment (main route)
  const firstSegment = decodedSegments[0];
  
  // Debug in development
  if (process.env.NODE_ENV === 'development') {
    console.log('Route translation:', {
      originalPath: pathname,
      decodedPath: decodedPathname,
      normalizedPath,
      pathParts,
      routeSegments,
      decodedSegments,
      firstSegment,
      targetLang,
      hasTranslation: !!translations[firstSegment],
      translation: translations[firstSegment]
    });
  }
  
  // Check if translation exists
  if (translations[firstSegment]) {
    decodedSegments[0] = translations[firstSegment];
  } else {
    // If no translation found, log warning
    if (process.env.NODE_ENV === 'development') {
      console.warn(`No translation found for route segment: "${firstSegment}" in language: ${targetLang}`);
      console.warn('Available translations:', Object.keys(translations));
    }
    // Keep the original segment if no translation found
    // This allows routes with params or unknown routes to work
  }

  // Build new path preserving all segments (including params like :id, :image, etc.)
  const newPath = `/${targetLang}/${decodedSegments.join('/')}`;
  
  return newPath;
};

/**
 * Gets the current language from pathname
 * @param {string} pathname - Current pathname
 * @returns {string} Language code ('en' or 'ar') or null
 */
export const getLanguageFromPath = (pathname) => {
  const pathParts = pathname.split('/').filter(Boolean);
  if (pathParts.length === 0) return null;
  
  const lang = pathParts[0];
  return (lang === 'en' || lang === 'ar') ? lang : null;
};

/**
 * Checks if a pathname is a valid route for a given language
 * @param {string} pathname - Pathname to check
 * @param {string} lang - Language to check against
 * @returns {boolean} True if pathname is valid for the language
 */
export const isValidRouteForLanguage = (pathname, lang) => {
  if (!pathname || !lang) return false;
  
  const pathLang = getLanguageFromPath(pathname);
  return pathLang === lang;
};
